import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from '@phosphor-icons/react';
import { fetchProducts, fetchClientsForProduct } from '../api/connectionRegistryApi';
import { useSupportingTools } from '../SupportingToolsContext';

function ConnectFlowPage() {
  const navigate = useNavigate();
  const { basePath, variant } = useSupportingTools();
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);

  useEffect(() => {
    (async () => {
      setLoadingProducts(true);
      try {
        setProducts(await fetchProducts(variant));
      } catch (e) {
        console.error('fetchProducts', e);
      } finally {
        setLoadingProducts(false);
      }
    })();
  }, []);

  const selectProduct = async (product) => {
    setSelectedProduct(product);
    setLoadingClients(true);
    try {
      setClients(await fetchClientsForProduct(product.product_key, variant));
    } catch (e) {
      console.error('fetchClientsForProduct', e);
    } finally {
      setLoadingClients(false);
    }
  };

  const selectClient = (client) => {
    navigate(`${basePath}/clients/${client.connection_id}`);
  };

  const back = () => {
    setSelectedProduct(null);
    setClients([]);
  };

  return (
    <div data-testid="connect-flow-page">
      <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', marginBottom: '4px' }}>Connect</h2>
      <p style={{ fontSize: '13px', color: '#6B7280', marginBottom: '20px' }}>
        Pick a product, then a client, to resolve their registry binding and confirm the connection.
      </p>

      {!selectedProduct ? (
        <div>
          <div style={{ fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '10px' }}>Step 1 — Select a product</div>
          {loadingProducts ? (
            <div>Loading...</div>
          ) : products.length === 0 ? (
            <div style={{ padding: '24px', background: 'white', border: '1px solid #E5E7EB', color: '#6B7280', fontSize: '14px' }} data-testid="products-empty">
              No products registered yet. Add a connection in the Registry tab first.
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '12px' }}>
              {products.map((p) => (
                <button
                  key={p.product_key}
                  onClick={() => selectProduct(p)}
                  className="btn-secondary"
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', padding: '16px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}
                  data-testid="product-option"
                >
                  <span>{p.product_label}</span>
                  <span
                    style={{
                      fontSize: '12px',
                      fontWeight: '600',
                      color: '#4B5563',
                      background: '#F3F4F6',
                      borderRadius: '999px',
                      padding: '2px 10px',
                    }}
                    data-testid="product-client-count"
                  >
                    {p.client_count} {p.client_count === 1 ? 'client' : 'clients'}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div>
          <button
            onClick={back}
            style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'transparent', border: 'none', color: '#002FA7', fontSize: '13px', cursor: 'pointer', marginBottom: '16px', padding: 0 }}
            data-testid="back-to-products-button"
          >
            <ArrowLeft size={14} /> Back to products
          </button>

          <div style={{ fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '10px' }}>
            Step 2 — Select a client for {selectedProduct.product_label}
          </div>
          {loadingClients ? (
            <div>Loading...</div>
          ) : clients.length === 0 ? (
            <div style={{ padding: '24px', background: 'white', border: '1px solid #E5E7EB', color: '#6B7280', fontSize: '14px' }} data-testid="clients-empty">
              No clients registered for this product yet.
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '12px' }}>
              {clients.map((c) => (
                <button
                  key={c.connection_id}
                  onClick={() => selectClient(c)}
                  className="btn-secondary"
                  style={{ padding: '16px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}
                  data-testid="client-option"
                >
                  {c.client_name}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ConnectFlowPage;
